import { Fragment, useMemo, type ReactElement } from 'react';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import { TableBreakdownStack } from '@/features/shared/components/tableSystem/TableMetaCells';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { collapseExecutionStatusCounts } from '@/features/shared/utils/projectStatusPresentation';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { ErrorOutline } from '@/ui';
import { SummaryTooltipContent } from '@/ui/composites/table/cells/summaryCellUtils';
import { getSummaryTooltipItems } from '@/ui/composites/table/tableUtils';
import { Box, SuitesIcon, TestsIcon, Tooltip } from '@/ui/primitives';
import {
  BlockOutlined,
  CancelOutlinedIcon,
  CheckCircleOutlineIcon,
  HelpIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  RemoveCircleIcon,
} from '@/ui/primitives/icons';
import type { TestSummaryItem } from '@/ui/composites/table/tableTypes';
import type { SvgIconComponent } from '@/ui/primitives';

const BREAKDOWN_TOOLTIP_ENTER_DELAY_MS = 250;
const EMPTY_SUMMARY_ITEMS: TestSummaryItem[] = [];

type BreakdownStatus = 'passed' | 'failed' | 'errored' | 'skipped' | 'untested';
type SkippedIconVariant = 'filled' | 'outlined';

type TableStatusBreakdownCellProps = {
  entity: 'suite' | 'test' | 'execution';
  totalIconEntity?: 'suite' | 'test' | 'execution';
  counts: {
    passed: number;
    failed: number;
    errored: number;
    skipped: number;
    untested: number;
  };
  summaryItems?: TestSummaryItem[] | (() => TestSummaryItem[]);
  summaryUnitLabel?: 'suite' | 'test' | 'execution';
  totalLinkMode?: 'detail' | 'execution';
  showSummaryTimestamps?: boolean;
  showSummaryDateHeaders?: boolean;
  disableSummaryLinks?: boolean;
  groupErroredWithFailed?: boolean;
};

type TableStatusBreakdownTooltipRowProps = {
  icon: ReactElement;
  content: string;
  summaryItems?: TableStatusBreakdownCellProps['summaryItems'];
  summaryUnitLabel: string | null;
  summaryStatus?: BreakdownStatus;
  linkMode: 'detail' | 'execution';
  entity: TableStatusBreakdownCellProps['entity'];
  skippedIconVariant: SkippedIconVariant;
  showSummaryTimestamps: boolean;
  showSummaryDateHeaders: boolean;
  disableSummaryLinks: boolean;
};

const formatPercent = (count: number, total: number): string => {
  if (total <= 0) return '0%';
  return `${Math.round((count / total) * 100)}%`;
};

type StatusIconComponent = SvgIconComponent;

const getBreakdownStatusIconComponent = (
  status: BreakdownStatus,
  skippedIconVariant: SkippedIconVariant,
): StatusIconComponent => {
  if (status === 'untested') return HelpIcon;
  if (status === 'passed') return CheckCircleOutlineIcon;
  if (status === 'failed') return CancelOutlinedIcon;
  if (status === 'errored') return ErrorOutline;
  return skippedIconVariant === 'outlined' ? BlockOutlined : RemoveCircleIcon;
};

const getEntityIconComponent = (
  entity: TableStatusBreakdownCellProps['entity'],
): StatusIconComponent =>
  entity === 'suite'
    ? SuitesIcon
    : entity === 'test'
      ? TestsIcon
      : PlayCircleFilledWhiteOutlinedIcon;

const getStatusIconColor = (
  status: BreakdownStatus,
): ((theme: Parameters<typeof resolveSemanticColor>[0]) => string) => {
  if (status === 'passed') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'success');
  }
  if (status === 'failed') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'error');
  }
  if (status === 'errored') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'brown');
  }
  if (status === 'skipped') {
    return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
      resolveSemanticColor(theme, 'warning');
  }
  return (theme: Parameters<typeof resolveSemanticColor>[0]) =>
    resolveSemanticColor(theme, 'textSecondary');
};

const TableStatusBreakdownTooltipRow = ({
  icon,
  content,
  summaryItems,
  summaryUnitLabel,
  summaryStatus,
  linkMode,
  entity,
  skippedIconVariant,
  showSummaryTimestamps,
  showSummaryDateHeaders,
  disableSummaryLinks,
}: TableStatusBreakdownTooltipRowProps): ReactElement => {
  const rawSummaryItems = useMemo(() => {
    if (summaryUnitLabel === null) {
      return EMPTY_SUMMARY_ITEMS;
    }
    return typeof summaryItems === 'function'
      ? summaryItems()
      : (summaryItems ?? EMPTY_SUMMARY_ITEMS);
  }, [summaryItems, summaryUnitLabel]);
  const hasSummaryItems = rawSummaryItems.length > 0;
  const items = useMemo(() => {
    if (!hasSummaryItems || summaryUnitLabel === null) {
      return [];
    }
    return getSummaryTooltipItems(rawSummaryItems, {
      unitLabel: summaryUnitLabel,
      status: summaryStatus,
    });
  }, [hasSummaryItems, rawSummaryItems, summaryStatus, summaryUnitLabel]);
  const tooltipHasItems = items.length > 0;

  return (
    <Tooltip
      title={
        tooltipHasItems ? (
          <SummaryTooltipContent
            items={items}
            linkMode={linkMode}
            skippedIconVariant={skippedIconVariant}
            showDetailLabel={entity !== 'execution'}
            showTimestamp={showSummaryTimestamps}
            showDateHeaders={showSummaryDateHeaders}
            disableLinks={disableSummaryLinks}
          />
        ) : (
          ''
        )
      }
      enterDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      enterNextDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      disableHoverListener={!tooltipHasItems}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
            width: 'fit-content',
            fontSize: '15px',
            lineHeight: 1.4,
            py: 0,
            pr: 0,
          },
        },
      }}
    >
      <Box
        component="span"
        sx={{ display: 'inline-block', width: 'fit-content', maxWidth: '100%' }}
      >
        <TableBreakdownLine
          icon={icon}
          content={content}
          ariaLabel={tooltipHasItems ? undefined : content}
          disableOverflowTooltip={tooltipHasItems}
        />
      </Box>
    </Tooltip>
  );
};

export const TableStatusBreakdownCell = ({
  entity,
  totalIconEntity,
  counts,
  summaryItems,
  summaryUnitLabel,
  totalLinkMode = 'detail',
  showSummaryTimestamps = true,
  showSummaryDateHeaders = entity === 'execution',
  disableSummaryLinks = false,
  groupErroredWithFailed,
}: TableStatusBreakdownCellProps): ReactElement => {
  const statusPresentation = useProjectStatusPresentation();
  const resolvedGroupErroredWithFailed =
    groupErroredWithFailed ?? statusPresentation.groupErroredWithFailed;
  const displayCounts = collapseExecutionStatusCounts(counts, resolvedGroupErroredWithFailed);
  const total =
    displayCounts.passed +
    displayCounts.failed +
    displayCounts.errored +
    displayCounts.skipped +
    displayCounts.untested;
  const entityLabel =
    entity === 'suite'
      ? total === 1
        ? 'suite'
        : 'suites'
      : entity === 'test'
        ? total === 1
          ? 'test'
          : 'tests'
        : total === 1
          ? 'run'
          : 'runs';
  const EntityIcon = getEntityIconComponent(totalIconEntity ?? entity);
  const rows = [
    {
      key: 'passed',
      count: displayCounts.passed,
      label: `${displayCounts.passed.toLocaleString()} passed (${formatPercent(displayCounts.passed, total)})`,
    },
    {
      key: 'failed',
      count: displayCounts.failed,
      label: `${displayCounts.failed.toLocaleString()} failed (${formatPercent(displayCounts.failed, total)})`,
    },
    {
      key: 'errored',
      count: displayCounts.errored,
      label: `${displayCounts.errored.toLocaleString()} errored (${formatPercent(displayCounts.errored, total)})`,
    },
    {
      key: 'skipped',
      count: displayCounts.skipped,
      label: `${displayCounts.skipped.toLocaleString()} skipped (${formatPercent(displayCounts.skipped, total)})`,
    },
    {
      key: 'untested',
      count: displayCounts.untested,
      label: `${displayCounts.untested.toLocaleString()} untested (${formatPercent(displayCounts.untested, total)})`,
    },
  ] satisfies Array<{ key: BreakdownStatus; count: number; label: string }>;
  const visibleRows = rows.filter((row) => row.count > 0);
  const resolvedSummaryUnitLabel =
    summaryUnitLabel ?? (entity === 'suite' || entity === 'test' ? entity : null);
  const skippedIconVariant: SkippedIconVariant = 'outlined';

  return (
    <TableBreakdownStack>
      <TableStatusBreakdownTooltipRow
        icon={
          <EntityIcon
            sx={{
              fontSize: (theme) => theme.app.iconSizes.sm,
              flexShrink: 0,
              color: (theme) => resolveSemanticColor(theme, 'textSecondary'),
              transform: 'translateY(2px)',
            }}
          />
        }
        content={`${total.toLocaleString()} ${entityLabel}`}
        summaryItems={summaryItems}
        summaryUnitLabel={resolvedSummaryUnitLabel}
        linkMode={totalLinkMode}
        entity={entity}
        skippedIconVariant={skippedIconVariant}
        showSummaryTimestamps={showSummaryTimestamps}
        showSummaryDateHeaders={showSummaryDateHeaders}
        disableSummaryLinks={disableSummaryLinks}
      />
      {visibleRows.map((row) => {
        const StatusIcon = getBreakdownStatusIconComponent(row.key, skippedIconVariant);
        const statusIconColor = getStatusIconColor(row.key);
        return (
          <Fragment key={row.key}>
            <TableStatusBreakdownTooltipRow
              icon={
                <StatusIcon
                  sx={{
                    fontSize: (theme) => theme.app.iconSizes.sm,
                    flexShrink: 0,
                    color: statusIconColor,
                    transform: 'translateY(2px)',
                  }}
                />
              }
              content={row.label}
              summaryItems={summaryItems}
              summaryUnitLabel={resolvedSummaryUnitLabel}
              summaryStatus={row.key}
              linkMode="execution"
              entity={entity}
              skippedIconVariant={skippedIconVariant}
              showSummaryTimestamps={showSummaryTimestamps}
              showSummaryDateHeaders={showSummaryDateHeaders}
              disableSummaryLinks={disableSummaryLinks}
            />
          </Fragment>
        );
      })}
    </TableBreakdownStack>
  );
};
